import Phaser from 'phaser';
import { Direction } from '../data/world-config';
import { MONSTERS, type MonsterSpecies } from '../data/monsters';
import { Player } from '../entities/Player';
import { CombatSystem } from '../systems/CombatSystem';
import { DayNightSystem } from '../systems/DayNightSystem';

// ---------------------------------------------------------------------------
// A stripped-down monster for the arena (no farm dependencies)
// ---------------------------------------------------------------------------
interface ArenaMonster {
  id: string;
  species: MonsterSpecies;
  sprite: Phaser.Physics.Arcade.Sprite;
  hp: number;
  maxHp: number;
  hpBar: Phaser.GameObjects.Graphics;
  lastAttackTime: number;
  dead: boolean;
  stunned: boolean;
  stunEndTime: number;
}

// ---------------------------------------------------------------------------
// ArenaScene – Battle-only mode for practicing combat
// ---------------------------------------------------------------------------
export class ArenaScene extends Phaser.Scene {
  player!: Player;
  combatSystem!: CombatSystem;
  dayNightSystem!: DayNightSystem;

  // We expose these so CombatSystem can reference them (it expects GameScene shape)
  nightMonsterSystem: null = null;
  farmSystem: null = null;

  private monsters: ArenaMonster[] = [];
  private monsterIdCounter: number = 0;

  // Wave state
  private wave: number = 0;
  private monstersKilled: number = 0;
  private totalKills: number = 0;
  private waveActive: boolean = false;
  private waveDelay: number = 0;
  private monstersToSpawn: number = 0;

  // Arena bounds (pixels)
  private arenaW: number = 400;
  private arenaH: number = 300;
  private arenaX: number = 0;
  private arenaY: number = 0;

  // Timer
  private elapsedTime: number = 0;

  // HUD elements
  private waveText!: Phaser.GameObjects.Text;
  private killText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private comboCountText!: Phaser.GameObjects.Text;
  private comboLabelText!: Phaser.GameObjects.Text;
  private comboContainer!: Phaser.GameObjects.Container;
  private comboFadeTween?: Phaser.Tweens.Tween;
  private hpBarGraphics!: Phaser.GameObjects.Graphics;
  private hpText!: Phaser.GameObjects.Text;
  private energyBarGraphics!: Phaser.GameObjects.Graphics;
  private energyText!: Phaser.GameObjects.Text;
  private waveAnnounce!: Phaser.GameObjects.Text;

  // Key tracking
  private keysDown: Set<string> = new Set();
  private onKeyDown!: (e: KeyboardEvent) => void;
  private onKeyUp!: (e: KeyboardEvent) => void;

  // Touch controls
  private isTouchDevice: boolean = false;
  private joystickContainer!: Phaser.GameObjects.Container;
  private joystickThumb!: Phaser.GameObjects.Arc;
  private joystickPointer: Phaser.Input.Pointer | null = null;
  private joystickRadius: number = 40;
  private touchDirX: number = 0;
  private touchDirY: number = 0;
  private touchButtons: Phaser.GameObjects.Container[] = [];
  private blockBtnBg?: Phaser.GameObjects.Arc;
  private isHoldingBlock: boolean = false;

  constructor() {
    super({ key: 'ArenaScene' });
  }

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Arena area (centered, with border)
    this.arenaW = width - 80;
    this.arenaH = height - 120;
    this.arenaX = 40;
    this.arenaY = 50;

    this.wave = 0;
    this.monstersKilled = 0;
    this.totalKills = 0;
    this.elapsedTime = 0;
    this.monsters = [];
    this.monsterIdCounter = 0;

    // Draw arena background
    this.createArenaBackground(width, height);

    // Create player in arena center
    const cx = this.arenaX + this.arenaW / 2;
    const cy = this.arenaY + this.arenaH / 2;
    this.player = new Player(this, cx, cy);
    this.player.sprite.setCollideWorldBounds(false); // We'll handle bounds ourselves

    // Camera (no zoom, fixed)
    this.cameras.main.setZoom(1);

    // Physics world bounds = arena
    this.physics.world.setBounds(this.arenaX + 8, this.arenaY + 8, this.arenaW - 16, this.arenaH - 16);
    this.player.sprite.setCollideWorldBounds(true);

    // Minimal DayNightSystem stub so CombatSystem doesn't crash
    this.dayNightSystem = {
      isNight: () => true,
      getDayCount: () => this.wave,
      update: () => {},
    } as unknown as DayNightSystem;

    // Initialize combat (it reads this.player, this.nightMonsterSystem, this.events)
    this.combatSystem = new CombatSystem(this as unknown as import('./GameScene').GameScene);

    // Override CombatSystem's monster check — we provide our own getAliveMonsters
    // We'll patch the nightMonsterSystem reference
    (this as any).nightMonsterSystem = {
      getAliveMonsters: () => this.getAliveMonsters(),
      hitMonster: (m: ArenaMonster, damage: number, knockback: number, type: string) => {
        this.hitMonster(m, damage, knockback, type);
      },
    };

    // Set up keyboard
    this.setupInput();

    // Touch controls (auto-detected)
    this.isTouchDevice = this.sys.game.device.input.touch;
    if (this.isTouchDevice) {
      this.createTouchJoystick(width, height);
      this.createTouchButtons(width, height);
    }

    // Create HUD
    this.createHUD(width, height);

    // Listen for combat events
    this.events.on('player-damaged', (damage: number) => {
      // Already handled by CombatSystem
    });

    this.events.on('monster-killed', (data: { xp: number; coins: number; speciesId: string }) => {
      this.monstersKilled++;
      this.totalKills++;
      this.killText.setText(`Kills: ${this.totalKills}`);
    });

    this.events.on('combo-update', (data: { count: number; type: string }) => {
      this.updateComboCounter(data.count, data.type);
    });

    this.events.on('notification', (msg: string) => {
      // Show brief notification
      this.showNotification(msg);
    });

    this.events.on('combat-stats-update', (stats: any) => {
      this.updateHUD(stats);
    });

    // Start first wave after a short intro
    this.announceWave('BATTLE MODE', 'Get ready to fight!', () => {
      this.startNextWave();
    });
  }

  private createArenaBackground(width: number, height: number): void {
    const bg = this.add.graphics();

    // Dark background
    bg.fillStyle(0x0a0a1a, 1);
    bg.fillRect(0, 0, width, height);

    // Arena floor
    bg.fillStyle(0x1a2a1e, 1);
    bg.fillRect(this.arenaX, this.arenaY, this.arenaW, this.arenaH);

    // Floor detail: subtle grid
    bg.lineStyle(1, 0x223322, 0.3);
    const gridSize = 32;
    for (let x = this.arenaX; x <= this.arenaX + this.arenaW; x += gridSize) {
      bg.lineBetween(x, this.arenaY, x, this.arenaY + this.arenaH);
    }
    for (let y = this.arenaY; y <= this.arenaY + this.arenaH; y += gridSize) {
      bg.lineBetween(this.arenaX, y, this.arenaX + this.arenaW, y);
    }

    // Arena border — fighting game style
    bg.lineStyle(3, 0xffaa00, 0.8);
    bg.strokeRect(this.arenaX, this.arenaY, this.arenaW, this.arenaH);

    // Corner decorations
    const cornerSize = 12;
    const corners = [
      [this.arenaX, this.arenaY],
      [this.arenaX + this.arenaW - cornerSize, this.arenaY],
      [this.arenaX, this.arenaY + this.arenaH - cornerSize],
      [this.arenaX + this.arenaW - cornerSize, this.arenaY + this.arenaH - cornerSize],
    ];
    corners.forEach(([cx, cy]) => {
      bg.fillStyle(0xffaa00, 0.6);
      bg.fillRect(cx, cy, cornerSize, cornerSize);
    });

    // Title bar background
    bg.fillStyle(0x000000, 0.8);
    bg.fillRect(0, 0, width, 45);

    // Bottom bar background
    bg.fillStyle(0x000000, 0.6);
    bg.fillRect(0, height - 60, width, 60);
  }

  private createHUD(width: number, height: number): void {
    // Top bar: wave, kills, timer
    this.add.text(10, 8, 'BATTLE MODE', {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffaa00', fontStyle: 'bold',
    });

    this.waveText = this.add.text(10, 26, 'Wave: 0', {
      fontFamily: 'monospace', fontSize: '11px', color: '#ffffff',
    });

    this.killText = this.add.text(140, 26, 'Kills: 0', {
      fontFamily: 'monospace', fontSize: '11px', color: '#6aaa3a',
    });

    this.timerText = this.add.text(width - 10, 8, '0:00', {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff',
    });
    this.timerText.setOrigin(1, 0);

    // HP bar (top right area)
    const hpLabel = this.add.text(width - 200, 26, 'HP', {
      fontFamily: 'monospace', fontSize: '9px', color: '#d4573b',
    });
    this.hpBarGraphics = this.add.graphics();
    this.hpText = this.add.text(width - 10, 26, '100/100', {
      fontFamily: 'monospace', fontSize: '9px', color: '#ffffff',
    });
    this.hpText.setOrigin(1, 0);

    // Energy bar
    const enLabel = this.add.text(width - 200, 37, 'EN', {
      fontFamily: 'monospace', fontSize: '9px', color: '#44aaff',
    });
    this.energyBarGraphics = this.add.graphics();
    this.energyText = this.add.text(width - 10, 37, '100', {
      fontFamily: 'monospace', fontSize: '9px', color: '#44aaff',
    });
    this.energyText.setOrigin(1, 0);

    this.drawHpBar(100, 100);
    this.drawEnergyBar(100, 100);

    // Bottom bar: controls hint (different for touch vs keyboard)
    if (!this.isTouchDevice) {
      this.add.text(width / 2, height - 45, 'J=Light  K=Heavy  L=Special  Shift=Block  Shift+Dir=Dodge', {
        fontFamily: 'monospace', fontSize: '9px', color: '#888888',
      }).setOrigin(0.5, 0);

      this.add.text(width / 2, height - 30, 'ESC = Return to Menu', {
        fontFamily: 'monospace', fontSize: '9px', color: '#666666',
      }).setOrigin(0.5, 0);
    }

    // Combo counter (right side)
    this.comboContainer = this.add.container(width - 60, height / 2 - 20);
    this.comboContainer.setDepth(110);
    this.comboContainer.setAlpha(0);

    this.comboCountText = this.add.text(0, 0, '0', {
      fontFamily: 'monospace', fontSize: '28px', color: '#ffaa00',
      stroke: '#000000', strokeThickness: 4, fontStyle: 'bold',
    });
    this.comboCountText.setOrigin(0.5, 0.5);
    this.comboContainer.add(this.comboCountText);

    this.comboLabelText = this.add.text(0, 20, 'COMBO', {
      fontFamily: 'monospace', fontSize: '9px', color: '#ffffff',
      stroke: '#000000', strokeThickness: 3,
    });
    this.comboLabelText.setOrigin(0.5, 0.5);
    this.comboContainer.add(this.comboLabelText);

    // Wave announce text (center, hidden)
    this.waveAnnounce = this.add.text(width / 2, height / 2 - 30, '', {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffaa00',
      stroke: '#000000', strokeThickness: 5, fontStyle: 'bold',
    });
    this.waveAnnounce.setOrigin(0.5, 0.5);
    this.waveAnnounce.setDepth(200);
    this.waveAnnounce.setAlpha(0);
  }

  private setupInput(): void {
    const gameKeys = new Set([
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'KeyW', 'KeyA', 'KeyS', 'KeyD',
      'KeyJ', 'KeyK', 'KeyL',
      'ShiftLeft', 'ShiftRight',
      'Space', 'Escape',
    ]);

    this.onKeyDown = (e: KeyboardEvent) => {
      if (gameKeys.has(e.code)) {
        e.preventDefault();
        const wasDown = this.keysDown.has(e.code);
        this.keysDown.add(e.code);

        if (!wasDown && e.code === 'Escape') {
          this.exitArena();
        }
      }
    };

    this.onKeyUp = (e: KeyboardEvent) => {
      if (gameKeys.has(e.code)) {
        e.preventDefault();
        this.keysDown.delete(e.code);
      }
    };

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  update(time: number, delta: number): void {
    this.elapsedTime += delta;

    // Timer display
    const secs = Math.floor(this.elapsedTime / 1000);
    const mins = Math.floor(secs / 60);
    const remSecs = secs % 60;
    this.timerText.setText(`${mins}:${remSecs.toString().padStart(2, '0')}`);

    // Player movement
    this.handleMovement();

    // Combat system update (energy regen, projectiles, combo timer)
    this.combatSystem.update(time, delta);

    // Update monsters
    this.updateMonsters(time);

    // Clean dead
    this.monsters = this.monsters.filter(m => !m.dead);

    // Check wave complete
    if (this.waveActive && this.monsters.filter(m => !m.dead).length === 0 && this.monstersToSpawn <= 0) {
      this.waveActive = false;
      this.waveDelay = 2000;

      // Brief celebration
      this.announceWave('WAVE CLEAR!', `${this.monstersKilled} monsters defeated`, () => {
        this.startNextWave();
      });
    }

    // Wave delay countdown (for spawning remaining monsters mid-wave)
    if (this.waveActive && this.monstersToSpawn > 0) {
      this.waveDelay -= delta;
      if (this.waveDelay <= 0) {
        this.spawnMonster();
        this.monstersToSpawn--;
        this.waveDelay = 1500; // gap between spawns
      }
    }
  }

  private handleMovement(): void {
    const speed = 120; // Faster in arena for more action
    let vx = 0;
    let vy = 0;

    // Keyboard input
    const left = this.keysDown.has('ArrowLeft') || this.keysDown.has('KeyA');
    const right = this.keysDown.has('ArrowRight') || this.keysDown.has('KeyD');
    const up = this.keysDown.has('ArrowUp') || this.keysDown.has('KeyW');
    const down = this.keysDown.has('ArrowDown') || this.keysDown.has('KeyS');

    if (left) vx = -speed;
    else if (right) vx = speed;
    if (up) vy = -speed;
    else if (down) vy = speed;

    // Touch joystick input (overrides keyboard if active)
    if (this.isTouchDevice && (this.touchDirX !== 0 || this.touchDirY !== 0)) {
      vx = this.touchDirX * speed;
      vy = this.touchDirY * speed;
    }

    if (vx !== 0 && vy !== 0) {
      vx *= Math.SQRT1_2;
      vy *= Math.SQRT1_2;
    }

    // Feed direction input for specials
    if (vx !== 0 || vy !== 0) {
      this.combatSystem.feedDirectionInput(this.player.direction);

      // Dodge (keyboard: shift+dir, touch: block+dir)
      const holdingBlock = this.keysDown.has('ShiftLeft') || this.keysDown.has('ShiftRight') || this.isHoldingBlock;
      if (holdingBlock) {
        if (this.combatSystem.tryDodge(vx, vy)) {
          return;
        }
      }
    }

    this.player.move(vx, vy);
  }

  // ===== WAVE MANAGEMENT =====

  private startNextWave(): void {
    this.wave++;
    this.monstersKilled = 0;
    this.waveText.setText(`Wave: ${this.wave}`);

    // Determine wave composition
    const baseCount = Math.min(2 + this.wave, 10);
    const totalForWave = baseCount;

    // Spawn first batch immediately, rest on timer
    const immediate = Math.min(3, totalForWave);
    this.monstersToSpawn = totalForWave - immediate;
    this.waveDelay = 2000;

    for (let i = 0; i < immediate; i++) {
      this.spawnMonster();
    }

    this.waveActive = true;

    this.announceWave(`WAVE ${this.wave}`, `${totalForWave} monsters incoming!`, null);
  }

  private spawnMonster(): void {
    // Pick monster type based on wave (mimics MONSTER_WAVES progression)
    let pool: string[];
    if (this.wave <= 2) pool = ['piranha_plant', 'thorn_crawler'];
    else if (this.wave <= 4) pool = ['piranha_plant', 'thorn_crawler', 'fungal_brute'];
    else if (this.wave <= 6) pool = ['thorn_crawler', 'fungal_brute', 'wilt_wraith'];
    else pool = ['fungal_brute', 'wilt_wraith', 'root_titan'];

    const speciesId = pool[Math.floor(Math.random() * pool.length)];
    const species = MONSTERS[speciesId];
    if (!species) return;

    // Scale HP with wave
    const hpScale = 1 + (this.wave - 1) * 0.15;

    // Spawn from arena edges
    const side = Math.floor(Math.random() * 4);
    let sx: number;
    let sy: number;
    const margin = 20;

    switch (side) {
      case 0: // top
        sx = this.arenaX + margin + Math.random() * (this.arenaW - margin * 2);
        sy = this.arenaY + margin;
        break;
      case 1: // right
        sx = this.arenaX + this.arenaW - margin;
        sy = this.arenaY + margin + Math.random() * (this.arenaH - margin * 2);
        break;
      case 2: // bottom
        sx = this.arenaX + margin + Math.random() * (this.arenaW - margin * 2);
        sy = this.arenaY + this.arenaH - margin;
        break;
      default: // left
        sx = this.arenaX + margin;
        sy = this.arenaY + margin + Math.random() * (this.arenaH - margin * 2);
        break;
    }

    const sprite = this.physics.add.sprite(sx, sy, 'monsters', species.spriteRow * 4);
    sprite.setDepth(9);
    sprite.setSize(12, 12);
    sprite.setOffset(2, 4);
    sprite.setCollideWorldBounds(true);

    const hpBar = this.add.graphics();
    hpBar.setDepth(11);

    const scaledHp = Math.floor(species.hp * hpScale);
    const monster: ArenaMonster = {
      id: `am_${this.monsterIdCounter++}`,
      species,
      sprite,
      hp: scaledHp,
      maxHp: scaledHp,
      hpBar,
      lastAttackTime: 0,
      dead: false,
      stunned: false,
      stunEndTime: 0,
    };

    this.monsters.push(monster);

    // Spawn flash
    sprite.setAlpha(0);
    const warning = this.add.circle(sx, sy, 10, 0xff4444, 0.4);
    warning.setDepth(8);
    this.tweens.add({
      targets: warning,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 500,
      onComplete: () => warning.destroy(),
    });
    this.tweens.add({
      targets: sprite,
      alpha: 1,
      duration: 400,
      ease: 'Power2',
    });
  }

  // ===== MONSTER AI =====

  private updateMonsters(time: number): void {
    for (const monster of this.monsters) {
      if (monster.dead) continue;

      // Stun check
      if (monster.stunned) {
        if (time >= monster.stunEndTime) {
          monster.stunned = false;
          monster.sprite.clearTint();
        } else {
          monster.sprite.setVelocity(0, 0);
          this.drawMonsterHpBar(monster);
          continue;
        }
      }

      const sprite = monster.sprite;
      const px = this.player.sprite.x;
      const py = this.player.sprite.y;
      const dx = px - sprite.x;
      const dy = py - sprite.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 16) {
        // Move toward player (slightly faster in arena)
        const speed = monster.species.speed * 1.2;
        sprite.setVelocity((dx / dist) * speed, (dy / dist) * speed);

        const frameOffset = Math.floor(time / 200) % 4;
        sprite.setFrame(monster.species.spriteRow * 4 + frameOffset);
        sprite.setFlipX(dx < 0);
      } else {
        sprite.setVelocity(0, 0);

        if (time - monster.lastAttackTime > 1500) {
          monster.lastAttackTime = time;
          this.monsterAttackPlayer(monster);
        }
      }

      this.drawMonsterHpBar(monster);
    }
  }

  private monsterAttackPlayer(monster: ArenaMonster): void {
    const damage = monster.species.attackPower;
    this.events.emit('player-damaged', damage);

    this.player.sprite.setTint(0xff4444);
    this.time.delayedCall(200, () => {
      if (!this.combatSystem.isBlocking) {
        this.player.sprite.clearTint();
      }
    });

    this.cameras.main.shake(80, 0.004);

    // Damage number on player
    const text = this.add.text(this.player.sprite.x, this.player.sprite.y - 10, `-${damage}`, {
      fontFamily: 'monospace', fontSize: '9px', color: '#ff4444',
      stroke: '#000000', strokeThickness: 3, fontStyle: 'bold',
    });
    text.setOrigin(0.5, 1);
    text.setDepth(20);
    this.tweens.add({
      targets: text,
      y: text.y - 20,
      alpha: 0,
      duration: 700,
      onComplete: () => text.destroy(),
    });
  }

  // ===== HIT HANDLING (called by CombatSystem via nightMonsterSystem proxy) =====

  hitMonster(monster: ArenaMonster, damage: number, knockback: number, attackType: string): void {
    monster.hp -= damage;

    // Knockback
    const dx = monster.sprite.x - this.player.sprite.x;
    const dy = monster.sprite.y - this.player.sprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    monster.sprite.setVelocity((dx / dist) * knockback, (dy / dist) * knockback);

    // Stun
    const stunDuration = attackType === 'special' ? 500 : attackType === 'heavy' ? 350 : 150;
    monster.stunned = true;
    monster.stunEndTime = this.time.now + stunDuration;

    this.time.delayedCall(stunDuration, () => {
      if (!monster.dead) monster.sprite.setVelocity(0, 0);
    });

    // Flash
    const flashColors: Record<string, number> = {
      light: 0xffffff, heavy: 0xffaa00, special: 0x44aaff,
    };
    monster.sprite.setTint(flashColors[attackType] ?? 0xffffff);
    this.time.delayedCall(100, () => {
      if (!monster.dead && !monster.stunned) monster.sprite.clearTint();
      else if (!monster.dead) monster.sprite.setTint(0xaaaaaa);
    });

    if (attackType === 'heavy') this.cameras.main.shake(60, 0.003);
    else if (attackType === 'special') this.cameras.main.shake(100, 0.005);

    if (monster.hp <= 0) {
      this.killMonster(monster);
    }
  }

  private killMonster(monster: ArenaMonster): void {
    monster.dead = true;

    this.events.emit('monster-killed', {
      xp: monster.species.xpReward,
      coins: monster.species.coinReward,
      speciesId: monster.species.id,
    });

    // Death burst
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const particle = this.add.circle(monster.sprite.x, monster.sprite.y, 2, 0xff4444, 0.8);
      particle.setDepth(15);
      this.tweens.add({
        targets: particle,
        x: monster.sprite.x + Math.cos(angle) * 25,
        y: monster.sprite.y + Math.sin(angle) * 25,
        alpha: 0,
        duration: 400,
        onComplete: () => particle.destroy(),
      });
    }

    this.tweens.add({
      targets: monster.sprite,
      alpha: 0, scaleX: 0.3, scaleY: 0.3, angle: 180,
      duration: 500,
      onComplete: () => {
        monster.sprite.destroy();
        monster.hpBar.destroy();
      },
    });
  }

  // ===== HELPERS =====

  getAliveMonsters(): ArenaMonster[] {
    return this.monsters.filter(m => !m.dead);
  }

  private drawMonsterHpBar(monster: ArenaMonster): void {
    const g = monster.hpBar;
    g.clear();
    const x = monster.sprite.x - 8;
    const y = monster.sprite.y - 12;
    const w = 16;
    const h = 2;

    g.fillStyle(0x333333);
    g.fillRect(x, y, w, h);

    const ratio = monster.hp / monster.maxHp;
    const color = ratio > 0.5 ? 0x6aaa3a : ratio > 0.25 ? 0xe8a038 : 0xd4573b;
    g.fillStyle(color);
    g.fillRect(x, y, w * ratio, h);
  }

  // ===== HUD UPDATES =====

  private drawHpBar(hp: number, maxHp: number): void {
    const g = this.hpBarGraphics;
    g.clear();
    const width = this.cameras.main.width;
    const x = width - 185;
    const y = 27;
    const w = 100;
    const h = 7;

    g.fillStyle(0x333333);
    g.fillRect(x, y, w, h);

    const ratio = hp / maxHp;
    const color = ratio > 0.5 ? 0x6aaa3a : ratio > 0.25 ? 0xe8a038 : 0xd4573b;
    g.fillStyle(color);
    g.fillRect(x, y, w * ratio, h);

    g.lineStyle(1, 0x666666);
    g.strokeRect(x, y, w, h);
  }

  private drawEnergyBar(energy: number, maxEnergy: number): void {
    const g = this.energyBarGraphics;
    g.clear();
    const width = this.cameras.main.width;
    const x = width - 185;
    const y = 38;
    const w = 100;
    const h = 6;

    g.fillStyle(0x222233);
    g.fillRect(x, y, w, h);

    const ratio = maxEnergy > 0 ? energy / maxEnergy : 0;
    g.fillStyle(ratio > 0.3 ? 0x44aaff : 0x2266aa);
    g.fillRect(x, y, w * ratio, h);

    g.lineStyle(1, 0x444466);
    g.strokeRect(x, y, w, h);
  }

  private updateHUD(stats: any): void {
    this.drawHpBar(stats.hp, stats.maxHp);
    this.drawEnergyBar(stats.energy, stats.maxEnergy);
    this.hpText.setText(`${stats.hp}/${stats.maxHp}`);
    this.energyText.setText(`${stats.energy}`);

    // Check for death
    if (stats.hp <= 0) {
      this.onArenaDefeat();
    }
  }

  private updateComboCounter(count: number, type: string): void {
    if (count <= 1) {
      if (this.comboFadeTween) this.comboFadeTween.stop();
      this.comboFadeTween = this.tweens.add({
        targets: this.comboContainer,
        alpha: 0,
        duration: 400,
      });
      return;
    }

    this.comboContainer.setAlpha(1);
    this.comboCountText.setText(`${count}`);

    if (count >= 10) {
      this.comboCountText.setColor('#ff4444');
      this.comboLabelText.setText('ULTRA!');
    } else if (count >= 7) {
      this.comboCountText.setColor('#ff44ff');
      this.comboLabelText.setText('SUPER!');
    } else if (count >= 4) {
      this.comboCountText.setColor('#44aaff');
      this.comboLabelText.setText('GREAT!');
    } else {
      this.comboCountText.setColor('#ffaa00');
      this.comboLabelText.setText('COMBO');
    }

    this.comboCountText.setScale(1.4);
    this.tweens.add({
      targets: this.comboCountText,
      scaleX: 1, scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    if (this.comboFadeTween) this.comboFadeTween.stop();
    this.comboFadeTween = this.tweens.add({
      targets: this.comboContainer,
      alpha: 0,
      duration: 600,
      delay: 1200,
    });
  }

  private announceWave(title: string, subtitle: string, onDone: (() => void) | null): void {
    this.waveAnnounce.setText(title);
    this.waveAnnounce.setAlpha(1);
    this.waveAnnounce.setScale(0.5);

    const subText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 + 10,
      subtitle,
      {
        fontFamily: 'monospace', fontSize: '12px', color: '#ffffff',
        stroke: '#000000', strokeThickness: 3,
      }
    );
    subText.setOrigin(0.5, 0.5);
    subText.setDepth(200);
    subText.setAlpha(0);

    this.tweens.add({
      targets: this.waveAnnounce,
      scaleX: 1, scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    this.tweens.add({
      targets: subText,
      alpha: 1,
      duration: 300,
      delay: 200,
    });

    this.tweens.add({
      targets: [this.waveAnnounce, subText],
      alpha: 0,
      duration: 500,
      delay: 1500,
      onComplete: () => {
        subText.destroy();
        if (onDone) onDone();
      },
    });

    this.cameras.main.flash(200, 255, 170, 0, false);
  }

  private notificationText?: Phaser.GameObjects.Text;

  private showNotification(msg: string): void {
    if (!this.notificationText) {
      this.notificationText = this.add.text(
        this.cameras.main.width / 2, this.arenaY + 15, '',
        {
          fontFamily: 'monospace', fontSize: '11px', color: '#e8a038',
          stroke: '#000000', strokeThickness: 3,
        }
      );
      this.notificationText.setOrigin(0.5, 0.5);
      this.notificationText.setDepth(150);
    }

    this.notificationText.setText(msg);
    this.notificationText.setAlpha(1);

    this.tweens.add({
      targets: this.notificationText,
      alpha: 0,
      duration: 1500,
      delay: 800,
    });
  }

  // ===== END CONDITIONS =====

  private onArenaDefeat(): void {
    this.waveActive = false;

    // Show game over
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, width, height);
    overlay.setDepth(300);

    const gameOverText = this.add.text(width / 2, height / 2 - 40, 'DEFEATED', {
      fontFamily: 'monospace', fontSize: '32px', color: '#ff4444',
      stroke: '#000000', strokeThickness: 6, fontStyle: 'bold',
    });
    gameOverText.setOrigin(0.5, 0.5);
    gameOverText.setDepth(301);

    const statsText = this.add.text(width / 2, height / 2 + 10,
      `Wave: ${this.wave}  |  Kills: ${this.totalKills}  |  Max Combo: ${this.combatSystem.maxCombo}`, {
      fontFamily: 'monospace', fontSize: '12px', color: '#ffffff',
      stroke: '#000000', strokeThickness: 3,
    });
    statsText.setOrigin(0.5, 0.5);
    statsText.setDepth(301);

    const secs = Math.floor(this.elapsedTime / 1000);
    const mins = Math.floor(secs / 60);
    const remSecs = secs % 60;
    const timeText = this.add.text(width / 2, height / 2 + 35,
      `Time: ${mins}:${remSecs.toString().padStart(2, '0')}`, {
      fontFamily: 'monospace', fontSize: '11px', color: '#aaaaaa',
      stroke: '#000000', strokeThickness: 2,
    });
    timeText.setOrigin(0.5, 0.5);
    timeText.setDepth(301);

    // Remove normal input first
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.combatSystem.shutdown();

    if (this.isTouchDevice) {
      // Touch: Retry and Menu tap buttons
      const retryBtn = this.createGameOverButton(width / 2 - 80, height / 2 + 65, 'RETRY', 0xd4573b, () => {
        this.restartArena();
      });
      retryBtn.setDepth(302);

      const menuBtn = this.createGameOverButton(width / 2 + 80, height / 2 + 65, 'MENU', 0x666666, () => {
        this.exitArena();
      });
      menuBtn.setDepth(302);
    } else {
      const retryText = this.add.text(width / 2, height / 2 + 70, 'Press SPACE to retry  |  ESC for menu', {
        fontFamily: 'monospace', fontSize: '11px', color: '#ffaa00',
      });
      retryText.setOrigin(0.5, 0.5);
      retryText.setDepth(301);

      this.tweens.add({
        targets: retryText,
        alpha: 0.4,
        duration: 800,
        yoyo: true,
        repeat: -1,
      });

      const handleRestart = (e: KeyboardEvent) => {
        if (e.code === 'Space') {
          window.removeEventListener('keydown', handleRestart);
          this.restartArena();
        } else if (e.code === 'Escape') {
          window.removeEventListener('keydown', handleRestart);
          this.exitArena();
        }
      };

      this.time.delayedCall(500, () => {
        window.addEventListener('keydown', handleRestart);
      });
    }
  }

  private createGameOverButton(x: number, y: number, label: string, color: number, callback: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const bg = this.add.graphics();
    const w = 120;
    const h = 40;
    bg.fillStyle(color, 0.9);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.lineStyle(2, 0xffffff, 0.5);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    container.add(bg);

    const text = this.add.text(0, 0, label, {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
    });
    text.setOrigin(0.5, 0.5);
    container.add(text);

    const hitZone = this.add.zone(0, 0, w, h).setInteractive({ useHandCursor: true });
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
      bg.lineStyle(2, 0xffffff, 0.8);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    });
    hitZone.on('pointerup', callback);

    return container;
  }

  // ===== TOUCH CONTROLS =====

  private createTouchJoystick(width: number, height: number): void {
    const cx = 80;
    const cy = height - 100;

    this.joystickContainer = this.add.container(cx, cy);
    this.joystickContainer.setDepth(300);

    // Base circle
    const base = this.add.circle(0, 0, this.joystickRadius, 0x000000, 0.3);
    base.setStrokeStyle(2, 0xffaa00, 0.5);
    this.joystickContainer.add(base);

    // D-pad hints
    const hintDist = this.joystickRadius * 0.55;
    const arrows = [
      { x: 0, y: -hintDist, label: '\u25B2' },
      { x: 0, y: hintDist, label: '\u25BC' },
      { x: -hintDist, y: 0, label: '\u25C0' },
      { x: hintDist, y: 0, label: '\u25B6' },
    ];
    arrows.forEach(a => {
      const t = this.add.text(a.x, a.y, a.label, {
        fontFamily: 'monospace', fontSize: '10px', color: '#ffffff',
      });
      t.setOrigin(0.5, 0.5);
      t.setAlpha(0.2);
      this.joystickContainer.add(t);
    });

    // Thumb knob
    this.joystickThumb = this.add.circle(0, 0, 18, 0xffaa00, 0.6);
    this.joystickThumb.setStrokeStyle(2, 0xffcc44, 0.8);
    this.joystickContainer.add(this.joystickThumb);

    // Hit zone
    const hitZone = this.add.circle(0, 0, this.joystickRadius + 20, 0x000000, 0.001);
    hitZone.setInteractive();
    this.joystickContainer.add(hitZone);

    // Pointer events
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.x < width * 0.4 && !this.joystickPointer) {
        this.joystickPointer = pointer;
        this.updateJoystickThumb(pointer);
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer === this.joystickPointer) {
        this.updateJoystickThumb(pointer);
      }
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer === this.joystickPointer) {
        this.joystickPointer = null;
        this.joystickThumb.setPosition(0, 0);
        this.touchDirX = 0;
        this.touchDirY = 0;
      }
    });
  }

  private updateJoystickThumb(pointer: Phaser.Input.Pointer): void {
    const cx = this.joystickContainer.x;
    const cy = this.joystickContainer.y;

    let dx = pointer.x - cx;
    let dy = pointer.y - cy;

    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > this.joystickRadius) {
      dx = (dx / dist) * this.joystickRadius;
      dy = (dy / dist) * this.joystickRadius;
    }

    this.joystickThumb.setPosition(dx, dy);

    const deadZone = 0.15;
    this.touchDirX = dx / this.joystickRadius;
    this.touchDirY = dy / this.joystickRadius;

    if (Math.abs(this.touchDirX) < deadZone) this.touchDirX = 0;
    if (Math.abs(this.touchDirY) < deadZone) this.touchDirY = 0;
  }

  private createTouchButtons(width: number, height: number): void {
    // Combat buttons on bottom-right — arranged in a diamond layout
    const btnSize = 44;
    const baseX = width - 75;
    const baseY = height - 105;

    // Light Attack (left of diamond) — fastest, most used, thumb-natural position
    this.createTouchButton(baseX - 50, baseY, 'J', 'Light', 0xffffff, btnSize, () => {
      this.combatSystem.swingSwordPublic();
    });

    // Heavy Attack (top of diamond)
    this.createTouchButton(baseX, baseY - 50, 'K', 'Heavy', 0xffaa00, btnSize, () => {
      this.combatSystem.heavyAttackPublic();
    });

    // Special Attack (right of diamond)
    this.createTouchButton(baseX + 50, baseY, 'L', 'Spcl', 0x44aaff, btnSize, () => {
      this.combatSystem.specialAttackPublic();
    });

    // Block (bottom of diamond) — hold to block, release to stop
    this.createBlockButton(baseX, baseY + 50, btnSize);

    // Menu button (top-right corner, small)
    const menuContainer = this.add.container(width - 30, 12);
    menuContainer.setDepth(300);

    const menuBg = this.add.circle(0, 0, 14, 0x666666, 0.5);
    menuBg.setStrokeStyle(1, 0xffffff, 0.3);
    menuContainer.add(menuBg);

    const menuLabel = this.add.text(0, 0, 'X', {
      fontFamily: 'monospace', fontSize: '12px', color: '#ffffff', fontStyle: 'bold',
    });
    menuLabel.setOrigin(0.5, 0.5);
    menuContainer.add(menuLabel);

    const menuHit = this.add.circle(0, 0, 18, 0x000000, 0.001);
    menuHit.setInteractive({ useHandCursor: true });
    menuContainer.add(menuHit);
    menuHit.on('pointerdown', () => this.exitArena());
    this.touchButtons.push(menuContainer);
  }

  private createTouchButton(
    x: number, y: number, letter: string, label: string,
    color: number, size: number, callback: () => void,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(300);

    const bg = this.add.circle(0, 0, size / 2, color, 0.15);
    bg.setStrokeStyle(2, color, 0.6);
    container.add(bg);

    const letterText = this.add.text(0, -4, letter, {
      fontFamily: 'monospace', fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    });
    letterText.setOrigin(0.5, 0.5);
    container.add(letterText);

    const labelText = this.add.text(0, 12, label, {
      fontFamily: 'monospace', fontSize: '7px', color: '#cccccc',
    });
    labelText.setOrigin(0.5, 0.5);
    container.add(labelText);

    const hitZone = this.add.circle(0, 0, size / 2 + 8, 0x000000, 0.001);
    hitZone.setInteractive();
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      bg.setAlpha(0.5);
      bg.setStrokeStyle(3, color, 1);
      callback();
    });
    hitZone.on('pointerup', () => {
      bg.setAlpha(0.15);
      bg.setStrokeStyle(2, color, 0.6);
    });
    hitZone.on('pointerout', () => {
      bg.setAlpha(0.15);
      bg.setStrokeStyle(2, color, 0.6);
    });

    this.touchButtons.push(container);
  }

  private createBlockButton(x: number, y: number, size: number): void {
    const container = this.add.container(x, y);
    container.setDepth(300);

    const color = 0x6688cc;
    this.blockBtnBg = this.add.circle(0, 0, size / 2, color, 0.15);
    this.blockBtnBg.setStrokeStyle(2, color, 0.6);
    container.add(this.blockBtnBg);

    const letterText = this.add.text(0, -4, '\u25A0', {
      fontFamily: 'monospace', fontSize: '14px', color: '#88aadd', fontStyle: 'bold',
    });
    letterText.setOrigin(0.5, 0.5);
    container.add(letterText);

    const labelText = this.add.text(0, 12, 'Block', {
      fontFamily: 'monospace', fontSize: '7px', color: '#cccccc',
    });
    labelText.setOrigin(0.5, 0.5);
    container.add(labelText);

    const hitZone = this.add.circle(0, 0, size / 2 + 8, 0x000000, 0.001);
    hitZone.setInteractive();
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      this.isHoldingBlock = true;
      this.blockBtnBg!.setAlpha(0.5);
      this.blockBtnBg!.setStrokeStyle(3, color, 1);
      this.combatSystem.startBlockPublic();
    });
    hitZone.on('pointerup', () => {
      this.isHoldingBlock = false;
      this.blockBtnBg!.setAlpha(0.15);
      this.blockBtnBg!.setStrokeStyle(2, color, 0.6);
      this.combatSystem.endBlockPublic();
    });
    hitZone.on('pointerout', () => {
      this.isHoldingBlock = false;
      this.blockBtnBg!.setAlpha(0.15);
      this.blockBtnBg!.setStrokeStyle(2, color, 0.6);
      this.combatSystem.endBlockPublic();
    });

    this.touchButtons.push(container);
  }

  // ===== END CONDITIONS =====

  private restartArena(): void {
    this.cleanup();
    this.scene.restart();
  }

  private exitArena(): void {
    this.cleanup();
    this.scene.start('MenuScene');
  }

  private cleanup(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.combatSystem?.shutdown();

    for (const monster of this.monsters) {
      monster.sprite?.destroy();
      monster.hpBar?.destroy();
    }
    this.monsters = [];

    // Clean up touch
    this.joystickPointer = null;
    this.touchDirX = 0;
    this.touchDirY = 0;
    this.isHoldingBlock = false;
    for (const btn of this.touchButtons) {
      btn?.destroy();
    }
    this.touchButtons = [];
    this.joystickContainer?.destroy();
  }

  shutdown(): void {
    this.cleanup();
  }
}
